import type { NormBlock, NormImageBlock, NormShapeBlock, NormTextBlock, SpreadTemplate, ThemeTemplate } from "./types";

function rid() {
  return `el_${Math.random().toString(16).slice(2)}`;
}

function unitId() {
  return `u_${Math.random().toString(16).slice(2)}`;
}

function cloneJson<T>(value: T): T {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function cloneTemplateElement(el: Record<string, unknown>) {
  const next = cloneJson(el || {});
  delete next._ghost;
  delete next._ghost_key;
  return {
    ...next,
    id: rid()
  };
}

function cloneSnapshotTemplatePages(theme: ThemeTemplate) {
  return (Array.isArray(theme.snapshot_pages) ? theme.snapshot_pages : []).map((unit) => ({
    ...cloneJson(unit),
    unit_id: unitId(),
    kind: String(unit?.kind || "single") === "spread" ? "spread" : "single",
    elements: (Array.isArray(unit?.elements) ? unit.elements : []).map(cloneTemplateElement)
  }));
}

// English comment: Convert normalized [0..1] box into mm coordinates inside a target box.
function mapNormBoxToMm(
  box: { x: number; y: number; w: number; h: number },
  target: { x: number; y: number; w: number; h: number }
) {
  return {
    x_mm: target.x + box.x * target.w,
    y_mm: target.y + box.y * target.h,
    w_mm: box.w * target.w,
    h_mm: box.h * target.h
  };
}

type PageMarginsMm = { left: number; right: number; top: number; bottom: number };

// English comment: Compute content box (margins) and bleed box for a unit.
function computeTemplateBoxes(args: {
  unitWmm: number;
  pageHmm: number;
  bleedMm: number;
  margins: PageMarginsMm;
}) {
  const { unitWmm, pageHmm, bleedMm, margins } = args;
  const contentBox = {
    x: margins.left,
    y: margins.top,
    w: Math.max(1, unitWmm - margins.left - margins.right),
    h: Math.max(1, pageHmm - margins.top - margins.bottom)
  };
  const bleedBox = {
    x: -bleedMm,
    y: -bleedMm,
    w: unitWmm + 2 * bleedMm,
    h: pageHmm + 2 * bleedMm
  };
  return { contentBox, bleedBox };
}

// English comment: Build a target box for anchored blocks on spreads.
function applyAnchorToBox(
  target: { x: number; y: number; w: number; h: number },
  anchor: "left" | "right" | "both" | undefined
) {
  if (!anchor || anchor === "both") return target;
  const half = target.w / 2;
  if (anchor === "left") {
    return { x: target.x, y: target.y, w: half, h: target.h };
  }
  return { x: target.x + half, y: target.y, w: half, h: target.h };
}

// English comment: Map normalized block to real element JSON (mm coords).
export function blocksToElementsMm(args: {
  blocks: NormBlock[];
  unitWmm: number;
  pageHmm: number;
  unitKind: "single" | "spread";
  bleedMm: number;
  margins: PageMarginsMm;
  textPresets: any;
  imageDefaults: any;
}) {
  const { blocks, unitWmm, pageHmm, textPresets, imageDefaults } = args;
  const isSpread = args.unitKind === "spread";
  const { contentBox, bleedBox } = computeTemplateBoxes({
    unitWmm,
    pageHmm,
    bleedMm: args.bleedMm,
    margins: args.margins
  });
  const els: any[] = [];

  for (const b of blocks) {
    if (b.type === "image") {
      const bb = b as NormImageBlock;
      const defaultClip = imageDefaults?.clip && imageDefaults.clip.type !== "none" ? imageDefaults.clip : { type: "none" };
      const base = bb.bleed ? bleedBox : contentBox;
      const target = isSpread ? applyAnchorToBox(base, bb.anchor) : base;
      const mm = mapNormBoxToMm(bb.box, target);

      els.push({
        id: rid(),
        type: "image",
        asset_id: null,
        placeholder: true,
        placeholder_kind: "image",
        x_mm: mm.x_mm,
        y_mm: mm.y_mm,
        w_mm: mm.w_mm,
        h_mm: mm.h_mm,
        fit: "cover",
        scale: 1,
        rotate_deg: 0,
        opacity: 1,
        clip: bb.maskShapeId ? { type: "shape", shape_id: bb.maskShapeId } : defaultClip
      });
      continue;
    }

    if (b.type === "shape") {
      const sb = b as NormShapeBlock;
      const target = isSpread ? applyAnchorToBox(contentBox, sb.anchor) : contentBox;
      const mm = mapNormBoxToMm(sb.box, target);
      els.push({
        id: rid(),
        type: "shape",
        shape_id: sb.shape_id,
        x_mm: mm.x_mm,
        y_mm: mm.y_mm,
        w_mm: mm.w_mm,
        h_mm: mm.h_mm,
        rotate_deg: 0,
        opacity: 1,
        style: { fill_hex: sb.fill_hex || "#111827", stroke_hex: "transparent", stroke_width_mm: 0 }
      });
      continue;
    }

    if (b.type === "text") {
      const tb = b as NormTextBlock;
      // English comment: Default template text styles (used when project presets are missing).
      const defaultStyleForRole = (role: "heading" | "body" | "list") => {
        if (role === "heading") {
          return {
            font_size_pt: 32,
            font_weight: 700,
          };
        }
        return {
          font_size_pt: 18,
          font_weight: 400,
        };
      };
      const preset = textPresets?.[tb.role] || {};
      const presetStyle = preset.style || {};
      const style = { ...defaultStyleForRole(tb.role), ...presetStyle };
      const target = isSpread ? applyAnchorToBox(contentBox, tb.anchor) : contentBox;
      const mm = mapNormBoxToMm(tb.box, target);

      els.push({
        id: rid(),
        type: "text",
        text_role: tb.role,
        list_marker: tb.listMarker || "bullet",
        x_mm: mm.x_mm,
        y_mm: mm.y_mm,
        w_mm: mm.w_mm,
        h_mm: mm.h_mm,
        rotate_deg: 0,
        text: tb.text || (tb.role === "heading" ? "Heading" : tb.role === "list" ? "Item 1\nItem 2" : "Text"),
        style
      });
    }
  }

  return els;
}

// English comment: Insert a spread unit at the end (book: before last single; spreads_only: append).
export function insertSpreadTemplateAtEnd(args: {
  docData: any;
  template: SpreadTemplate;
  pageWmm: number;
  pageHmm: number;
  contentLayout: "book" | "spreads_only";
  bleedMm: number;
  margins: PageMarginsMm;
  textPresets: any;
  imageDefaults: any;
}) {
  const { docData, template, pageWmm, pageHmm, contentLayout, bleedMm, margins, textPresets, imageDefaults } = args;
  const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
  const elements = blocksToElementsMm({
    blocks: template.blocks,
    unitWmm: pageWmm * 2,
    pageHmm,
    unitKind: "spread",
    bleedMm,
    margins,
    textPresets,
    imageDefaults
  });

  const newUnit = {
    unit_id: unitId(),
    kind: "spread",
    elements
  };

  let insertIndex = pages.length;
  if (contentLayout === "book" && pages.length >= 2) {
    insertIndex = pages.length - 1;
  }

  pages.splice(insertIndex, 0, newUnit);
  return { ...docData, pages };
}

// English comment: Apply a theme by rebuilding content units up to target_physical_pages (clamped).
export function applyThemeTemplate(args: {
  docData: any;
  theme: ThemeTemplate;
  pageWmm: number;
  pageHmm: number;
  contentLayout: "book" | "spreads_only";
  minPages: number;
  maxPages: number;
  bleedMm: number;
  margins: PageMarginsMm;
  textPresets: any;
  imageDefaults: any;
}) {
  const { docData, theme, pageWmm, pageHmm, contentLayout, minPages, maxPages, bleedMm, margins, textPresets, imageDefaults } = args;

  if (Array.isArray(theme.snapshot_pages) && theme.snapshot_pages.length > 0) {
    return {
      ...docData,
      pages: cloneSnapshotTemplatePages(theme)
    };
  }

  let target = theme.target_physical_pages;
  target = Math.max(minPages, Math.min(maxPages, target));
  if (contentLayout === "spreads_only" && target % 2 === 1) target += 1;

  const nextPages: any[] = [];

  const makeSingle = (blocks: NormBlock[]) => ({
    unit_id: unitId(),
    kind: "single",
    elements: blocksToElementsMm({ blocks, unitWmm: pageWmm, pageHmm, unitKind: "single", bleedMm, margins, textPresets, imageDefaults })
  });

  const makeSpread = (blocks: NormBlock[]) => ({
    unit_id: unitId(),
    kind: "spread",
    elements: blocksToElementsMm({ blocks, unitWmm: pageWmm * 2, pageHmm, unitKind: "spread", bleedMm, margins, textPresets, imageDefaults })
  });

  if (contentLayout === "book") {
    nextPages.push(makeSingle(theme.single_first || []));

    const remaining = target - 2;
    const spreads = Math.max(0, Math.floor(remaining / 2));

    for (let i = 0; i < spreads; i++) {
      const cycle = theme.spread_cycle?.length ? theme.spread_cycle : [[]];
      const blocks = cycle[i % cycle.length];
      nextPages.push(makeSpread(blocks));
    }

    nextPages.push(makeSingle(theme.single_last || []));
  } else {
    const spreads = Math.max(1, Math.floor(target / 2));
    for (let i = 0; i < spreads; i++) {
      const cycle = theme.spread_cycle?.length ? theme.spread_cycle : [[]];
      const blocks = cycle[i % cycle.length];
      nextPages.push(makeSpread(blocks));
    }
  }

  return { ...docData, pages: nextPages };
}
